import React, { useState } from 'react';
import type { FieldGroup } from '../types';
import { FieldRenderer } from './FieldRenderers';
import { processTemplate } from '../utils';

interface GroupRendererProps {
  group: FieldGroup;
  data: Record<string, unknown>;
  showLabels?: boolean;
  showEmptyFields?: boolean;
  showGroupTitles?: boolean;
  showGroupDescriptions?: boolean;
}

export const GroupRenderer: React.FC<GroupRendererProps> = ({
  group,
  data,
  showLabels = true,
  showEmptyFields = false,
  showGroupTitles = true,
  showGroupDescriptions = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(!group.isCollapsible);

  // Process templates in title and description
  const title = group.title ? processTemplate(group.title, data) : undefined;
  const description = group.description
    ? processTemplate(group.description, data)
    : undefined;

  const renderFields = () => {
    const fields = group.fields.map((field, index) => {
      if ('field' in field) {
        return (
          <FieldRenderer
            key={field.field}
            field={field}
            data={data}
            layout={group.layout === 'grid' ? 'row' : group.layout}
            showLabels={showLabels}
            showEmptyFields={showEmptyFields}
          />
        );
      }

      return (
        <GroupRenderer
          key={`group-${index}`}
          group={field}
          data={data}
          showLabels={showLabels}
          showEmptyFields={showEmptyFields}
          showGroupTitles={showGroupTitles}
          showGroupDescriptions={showGroupDescriptions}
        />
      );
    });

    switch (group.layout) {
      case 'grid':
        return (
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns:
                group.gridConfig?.templateColumns ||
                `repeat(${group.gridConfig?.columns || 2}, 1fr)`,
              gap: group.gridConfig?.gap || '1rem',
              rowGap: group.gridConfig?.rowGap || '1rem',
              columnGap: group.gridConfig?.columnGap || '1rem',
            }}
          >
            {fields}
          </div>
        );

      case 'column':
        return (
          <div
            className="flex flex-col gap-4"
            style={{ gap: `${group.spacing || 1}rem` }}
          >
            {fields}
          </div>
        );

      default:
        return (
          <div
            className="flex flex-wrap gap-4"
            style={{ gap: `${group.spacing || 1}rem` }}
          >
            {fields}
          </div>
        );
    }
  };

  return (
    <div className={group.className} style={group.style}>
      {showGroupTitles && title && (
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </h3>
          {group.isCollapsible && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              {isExpanded ? (
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              ) : (
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              )}
            </button>
          )}
        </div>
      )}

      {showGroupDescriptions && description && (
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          {description}
        </p>
      )}

      {isExpanded && renderFields()}
    </div>
  );
};
